import React, { useEffect, useState } from "react";
import API from "../services/api";

const CompletedBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal & Detailed Data State
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bids, setBids] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchCompletedBookings = async () => {
    try {
      setLoading(true);
      const res = await API.get("/completed-bookings");
      if (res.data.success) {
        setBookings(res.data.bookings);
      } else {
        setError("Failed to fetch completed bookings");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetails = async (bookingId) => {
    try {
      setDetailLoading(true);
      const res = await API.get(`/booking/${bookingId}`);
      if (res.data.success) {
        setSelectedBooking(res.data.booking);
        setBids(res.data.bids);
        setIsModalOpen(true);
      }
    } catch (err) {
      alert("Error fetching ride history details");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletedBookings();
  }, []);

  if (loading) return <div style={styles.loading}>Loading completed history...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Completed Bookings</h2>
          <p style={styles.subtitle}>Review successfully finished rides</p>
        </div>
        <div style={styles.badgeCount}>{bookings.length} Completed</div>
      </div>

      {/* GRID */}
      {bookings.length === 0 ? (
        <div style={styles.empty}>No completed bookings found</div>
      ) : (
        <div style={styles.grid}>
          {bookings.map((b) => (
            <div 
              key={b._id} 
              style={styles.card} 
              onClick={() => handleOpenDetails(b._id)}
            >
              <div style={styles.cardTop}>
                <span style={styles.bookingId}>{b._id.slice(-6).toUpperCase()}</span>
                <span style={styles.successBadge}>{b.status}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Customer</span>
                <span style={styles.value}>{b.user?.name || "N/A"}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Driver</span>
                <span style={styles.value}>{b.assignedDriver?.name || "N/A"}</span>
              </div>

              <div style={styles.vehicleBox}>
                <div style={styles.label}>Vehicle</div>
                <div style={styles.vehicleValue}>
                  {b.assignedDriver?.vehicleDetails?.model} • {b.assignedDriver?.vehicleDetails?.number}
                </div>
              </div>

              <div style={styles.footer}>
                Finished: {new Date(b.createdAt).toLocaleString()}
                {detailLoading && " • Fetching Details..."}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* RIDE DETAILS MODAL */}
      {isModalOpen && selectedBooking && (
        <div style={styles.overlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>Ride Summary: {selectedBooking._id.slice(-6).toUpperCase()}</h3>
              <button style={styles.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.infoGrid}>
                 <div style={styles.infoItem}>
                    <strong>Customer</strong>
                    <p>{selectedBooking.user?.name} ({selectedBooking.user?.phone})</p>
                 </div>
                 <div style={styles.infoItem}>
                    <strong>Driver</strong>
                    <p>{selectedBooking.assignedDriver?.name}</p>
                 </div>
              </div>

              <h4 style={styles.bidHeader}>Bidding History</h4>
              <div style={styles.bidTable}>
                <div style={styles.bidTableHeader}>
                  <span>Driver</span>
                  <span>Offer Price</span>
                </div>
                {bids.length === 0 ? (
                  <p style={{textAlign: 'center', padding: '10px', fontSize: '12px'}}>Direct assignment (No bids)</p>
                ) : (
                  bids.map((bid) => (
                    <div key={bid._id} style={styles.bidRow}>
                      <span>{bid.driver?.name}</span>
                      <span style={styles.bidPrice}>₹{bid.amount}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= STYLES ================= */

const styles = {
  // Existing Styles
  page: { padding: "20px", background: "#f5f7fb", minHeight: "100vh", fontFamily: "'Segoe UI', Roboto, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px" },
  title: { margin: 0, fontSize: "24px", color: "#111827" },
  subtitle: { margin: "4px 0 0 0", fontSize: "13px", color: "#6b7280" },
  badgeCount: { background: "#10b981", color: "white", padding: "8px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: "bold" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "20px" },
  card: { background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)", border: "1px solid #f0f0f0", cursor: "pointer" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  bookingId: { fontFamily: "monospace", fontSize: "12px", color: "#6b7280", background: "#f9fafb", padding: "4px 8px", borderRadius: "6px", border: "1px solid #eee" },
  successBadge: { background: "#dcfce7", color: "#15803d", padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase" },
  row: { display: "flex", justifyContent: "space-between", marginBottom: "12px" },
  vehicleBox: { background: "#f8fafc", padding: "10px", borderRadius: "8px", marginBottom: "12px", borderLeft: "4px solid #10b981" },
  label: { fontSize: "12px", color: "#6b7280", fontWeight: "500" },
  value: { fontSize: "13px", fontWeight: "600", color: "#1f2937" },
  vehicleValue: { fontSize: "12px", fontWeight: "500", color: "#374151", marginTop: "4px" },
  footer: { marginTop: "15px", fontSize: "11px", color: "#9ca3af", borderTop: "1px solid #f3f4f6", paddingTop: "12px" },
  loading: { padding: "50px", textAlign: "center", fontSize: "18px", color: "#10b981" },
  error: { color: "#b91c1c", padding: "20px", textAlign: "center", background: "#fef2f2", borderRadius: "12px", margin: "20px" },
  empty: { textAlign: "center", padding: "80px 20px", color: "#9ca3af", background: "#fff", borderRadius: "16px" },

  // Modal Styles
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", width: "95%", maxWidth: "500px", borderRadius: "20px", overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" },
  modalHeader: { padding: "20px", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" },
  closeBtn: { background: "none", border: "none", fontSize: "28px", cursor: "pointer", color: "#9ca3af" },
  modalBody: { padding: "20px" },
  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "25px" },
  infoItem: { fontSize: "13px" },
  bidHeader: { fontSize: "14px", fontWeight: "700", marginBottom: "12px", color: "#111827" },
  bidTable: { border: "1px solid #f3f4f6", borderRadius: "12px", overflow: "hidden" },
  bidTableHeader: { display: "flex", justifyContent: "space-between", padding: "10px 15px", background: "#f9fafb", fontSize: "11px", fontWeight: "bold", color: "#6b7280", textTransform: "uppercase" },
  bidRow: { display: "flex", justifyContent: "space-between", padding: "12px 15px", borderTop: "1px solid #f3f4f6", fontSize: "13px" },
  bidPrice: { fontWeight: "bold", color: "#10b981" }
};

export default CompletedBookings;